import React, { useMemo, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  FormControl,
  InputLabel,
  Chip,
  CircularProgress,
} from "@mui/material";
import BasePaper from "components/Base/BasePaper";
import AddressAutocomplete from "components/Base/AddressAutocomplete";
import { RectangularTextButton } from "components/Base/Buttons";

type AddressCandidate = {
  street_address: string;
  city: string;
  state: string;
  post_code: string;
  country: string;
  place_id: string;
  formatted_address: string;
  latitude?: number;
  longitude?: number;
  confidence?: number;
};

type ResolutionRow = {
  row: number;
  raw_parts: Record<string, string>;
  query: string;
  candidates: AddressCandidate[];
  error?: string | null;
};

type Resolution = {
  total_rows: number;
  rows: ResolutionRow[];
};

type AddressOverride =
  | { mode: "skip" }
  | { mode: "use"; place_id: string };

interface Props {
  resolution: Resolution | null;
  overrides: Record<string, AddressOverride>;
  setOverrides: React.Dispatch<
    React.SetStateAction<Record<string, AddressOverride>>
  >;
  uploading: boolean;
}

const formatRaw = (raw_parts: Record<string, string>) => {
  const parts = [
    raw_parts.street_address,
    raw_parts.city,
    raw_parts.post_code,
    raw_parts.country,
  ].filter(Boolean);

  return parts.join(", ");
};

export const StepVerifyAddresses: React.FC<Props> = ({
  resolution,
  overrides,
  setOverrides,
  uploading,
}) => {
  const [manualOpen, setManualOpen] = useState(false);
  const [manualRow, setManualRow] = useState<number | null>(null);
  const [manualValue, setManualValue] = useState("");
  const [showUnresolvedHighlight, setShowUnresolvedHighlight] = useState(false);

  const rows = resolution?.rows || [];

  const needsAttention = useMemo(() => {
    return rows.filter((r) => {
      const hasAnyInput = Object.values(r.raw_parts || {}).some(
        (v) => String(v || "").trim() !== ""
      );

      if (!hasAnyInput) return false;

      return !overrides[String(r.row)];
    });
  }, [rows, overrides]);

  const autoAcceptHighConfidence = () => {
    setShowUnresolvedHighlight(true);

    setOverrides((prev) => {
      const next = { ...prev };

      for (const r of rows) {
        const rowKey = String(r.row);
        if (next[rowKey]) continue;

        const hasAnyInput = Object.values(r.raw_parts || {}).some(
          (v) => String(v || "").trim() !== ""
        );

        if (!hasAnyInput) continue;

        const best = r.candidates?.[0];

        if (best?.place_id && (best.confidence ?? 0) >= 0.5) {
          next[rowKey] = { mode: "use", place_id: best.place_id };
        }
      }

      return next;
    });
  };

  const setSkip = (row: number) => {
    setOverrides((prev) => ({ ...prev, [String(row)]: { mode: "skip" } }));
  };

  const setUse = (row: number, place_id: string) => {
    setOverrides((prev) => ({
      ...prev,
      [String(row)]: { mode: "use", place_id },
    }));
  };

  const openManual = (row: number) => {
    setManualRow(row);
    setManualValue("");
    setManualOpen(true);
  };

  const saveManual = () => {
    setManualOpen(false);
    setManualRow(null);
  };

  if (uploading && !resolution) {
    return (
      <BasePaper>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 4,
            gap: 2,
          }}
        >
          <CircularProgress size={24} />
          <Typography>Resolving addresses...</Typography>
        </Box>
      </BasePaper>
    );
  }

  if (!resolution) {
    return (
      <BasePaper>
        <Typography variant="h6">Verify Addresses</Typography>
        <Typography color="text.secondary" sx={{ mt: 1 }}>
          No address resolution results yet.
        </Typography>
      </BasePaper>
    );
  }

  return (
    <Box>
      <BasePaper>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
        >
          <Box>
            <Typography variant="h6">Verify Addresses</Typography>
            <Typography color="text.secondary" sx={{ mt: 0.5 }}>
              Choose a suggested address for each row (or skip). You can also
              manually search.
            </Typography>
          </Box>

          <Box display="flex" gap={1} alignItems="center">
            <RectangularTextButton
              colour="clear"
              onClick={autoAcceptHighConfidence}
            >
              Auto-accept high confidence
            </RectangularTextButton>

            <Chip
              label={`${needsAttention.length} unresolved`}
              color={needsAttention.length === 0 ? "success" : "warning"}
              variant="outlined"
            />
          </Box>
        </Box>
      </BasePaper>

      <Box sx={{ mt: 2 }}>
        <BasePaper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={60}>
                  <strong>Row</strong>
                </TableCell>
                <TableCell>
                  <strong>Imported Address</strong>
                </TableCell>
                <TableCell>
                  <strong>Suggestion</strong>
                </TableCell>
                <TableCell width={220}>
                  <strong>Actions</strong>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {rows.map((r) => {
                const rowKey = String(r.row);
                const ov = overrides[rowKey];

                const hasAnyInput = Object.values(r.raw_parts || {}).some(
                  (v) => String(v || "").trim() !== ""
                );

                if (!hasAnyInput) {
                  return (
                    <TableRow key={rowKey}>
                      <TableCell>{r.row}</TableCell>
                      <TableCell colSpan={3} sx={{ color: "text.secondary" }}>
                        No address data in this row.
                      </TableCell>
                    </TableRow>
                  );
                }

                const isUnresolved = !ov;
                const shouldHighlight = showUnresolvedHighlight && isUnresolved;
                const selectedPlaceId = ov?.mode === "use" ? ov.place_id : "";

                return (
                  <TableRow
                    key={rowKey}
                    sx={{
                      backgroundColor: shouldHighlight
                        ? "rgba(255, 193, 7, 0.16)"
                        : "inherit",
                      "& td": {
                        borderColor: shouldHighlight
                          ? "warning.main"
                          : undefined,
                      },
                    }}
                  >
                    <TableCell>
                      <Typography fontWeight={shouldHighlight ? 700 : 400}>
                        {r.row}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography
                        sx={{
                          fontSize: 14,
                          fontWeight: shouldHighlight ? 600 : 400,
                        }}
                      >
                        {formatRaw(r.raw_parts) || "—"}
                      </Typography>

                      {r.error && (
                        <Typography
                          sx={{ fontSize: 12, color: "error.main", mt: 0.5 }}
                        >
                          {r.error}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      {r.candidates?.length ? (
                        <FormControl fullWidth size="small">
                          <InputLabel>Suggested match</InputLabel>
                          <Select
                            label="Suggested match"
                            value={selectedPlaceId}
                            onChange={(e) =>
                              setUse(r.row, String(e.target.value))
                            }
                          >
                            {r.candidates.map((c) => (
                              <MenuItem key={c.place_id} value={c.place_id}>
                                {c.formatted_address}
                                {typeof c.confidence === "number"
                                  ? ` (conf ${Math.round(c.confidence * 100)}%)`
                                  : ""}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
                          No suggestions found.
                        </Typography>
                      )}

                      {ov?.mode === "skip" && (
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: "text.secondary",
                            mt: 0.5,
                          }}
                        >
                          Marked as skipped.
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      <Box display="flex" gap={1} flexWrap="wrap">
                        <RectangularTextButton
                          colour="clear"
                          buttonSize="small"
                          onClick={() => openManual(r.row)}
                        >
                          Manual search
                        </RectangularTextButton>

                        <RectangularTextButton
                          colour="clear"
                          buttonSize="small"
                          textColor="inherit"
                          borderColor="currentColor"
                          onClick={() => setSkip(r.row)}
                        >
                          Skip
                        </RectangularTextButton>

                        {ov && (
                          <RectangularTextButton
                            colour="clear"
                            buttonSize="small"
                            textColor="inherit"
                            borderColor="transparent"
                            onClick={() =>
                              setOverrides((prev) => {
                                const next = { ...prev };
                                delete next[rowKey];
                                return next;
                              })
                            }
                          >
                            Clear
                          </RectangularTextButton>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </BasePaper>
      </Box>

      <Dialog
        open={manualOpen}
        onClose={() => setManualOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            overflow: "visible",
          },
        }}
      >
        <DialogTitle>Manual Address Search (Row {manualRow ?? "—"})</DialogTitle>

        <DialogContent
          sx={{
            overflow: "visible",
          }}
        >
          <Box sx={{ mt: 1, overflow: "visible" }}>
            <AddressAutocomplete
              label="Search address"
              value={manualValue}
              onChange={(v) => setManualValue(v)}
              onAddressSelected={(addr) => {
                if (manualRow != null) {
                  setUse(manualRow, addr.place_id);
                }
                saveManual();
              }}
            />

            <Typography sx={{ mt: 1, fontSize: 12, color: "text.secondary" }}>
              Select from suggestions.
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};